/**
 * DeploysShell — 2026-06-26. 5th view-mode shell: manage this tenant's
 * public deployments (list + undeploy).
 *
 * Mirrors KnowledgeShell chrome (TopBar + ResizableVoicePanel above a body)
 * so the tab switcher + voice strip stay visible. On mount it fetches the
 * tenant's deploys via deployStore.fetchDeploys (GET /api/deploys). Each row
 * has a two-click Undeploy (taking a public site down is destructive →
 * confirm before DELETE /api/deploys/{id}).
 */
import { Fragment, useEffect, useState } from 'react';
import { Trash2, Globe, ExternalLink, Fingerprint, ShieldCheck, ShieldAlert, Star, Undo2 } from 'lucide-react';
import { ResizableVoicePanel } from '../voice/ResizableVoicePanel';
import { useDeployStore, type CoherenceEntry } from '@/stores/deployStore';
import type { DeployListItem } from '@/types';
import { useUIStore } from '@/stores/uiStore';
import { useSpaContextStore } from '@/stores/spaContextStore';
import { buildDeploysSnapshot } from './deploysSnapshot';

/**
 * Gap A6 (2026-08-18) — expanded coherence report for one deploy row:
 * verdict chip, DB⇔vhost⇔docroot⇔cert checks, per-host SAN state. The
 * healfrommold split-brain class, previously invisible in this view.
 */
function CoherencePanel({ slug, entry }: { slug: string; entry: CoherenceEntry | undefined }) {
  if (!entry || entry.state === 'loading') {
    return <div className="text-vibe-text-secondary text-xs py-2">Probing live VPS state…</div>;
  }
  if (entry.state === 'error') {
    return (
      <div className="text-vibe-error text-xs py-2" data-testid={`coherence-error-${slug}`}>
        {entry.message}
      </div>
    );
  }
  const { report } = entry;
  return (
    <div className="py-3 pl-1 space-y-3" data-testid={`coherence-panel-${slug}`}>
      <div className="flex items-center gap-2">
        {report.coherent ? (
          <span
            data-testid={`coherence-verdict-${slug}`}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-vibe-success/20 text-vibe-success text-xs font-medium"
          >
            <ShieldCheck className="w-3.5 h-3.5" /> Coherent
          </span>
        ) : (
          <span
            data-testid={`coherence-verdict-${slug}`}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-vibe-error/20 text-vibe-error text-xs font-medium"
          >
            <ShieldAlert className="w-3.5 h-3.5" /> DRIFT
          </span>
        )}
        {report.renderedAt != null && (
          <span className="text-xs text-vibe-text-secondary">
            vhost rendered {new Date(report.renderedAt * 1000).toLocaleString()}
          </span>
        )}
      </div>
      <ul className="space-y-1">
        {report.checks.map((c) => (
          <li key={c.key} className="text-xs flex items-start gap-2">
            <span className={c.ok ? 'text-vibe-success' : 'text-vibe-error'}>
              {c.ok ? '✓' : '✗'}
            </span>
            <span className="font-mono text-vibe-text-secondary">{c.key}</span>
            <span className={c.ok ? 'text-vibe-text-secondary' : 'text-vibe-error'}>
              {c.detail}
            </span>
          </li>
        ))}
      </ul>
      {report.hosts.length > 0 && (
        <table className="text-xs w-full border-collapse">
          <thead>
            <tr className="text-left text-vibe-text-secondary border-b border-vibe-border/50">
              <th className="py-1 pr-3">Host</th>
              <th className="py-1 pr-3">Cert lineage</th>
              <th className="py-1 pr-3">SAN</th>
              <th className="py-1 pr-3">State</th>
            </tr>
          </thead>
          <tbody>
            {report.hosts.map((h) => (
              <tr key={h.fqdn}>
                <td className="py-1 pr-3 font-mono">
                  <span className="inline-flex items-center gap-1">
                    {h.fqdn}
                    {h.isCanonical && <Star className="w-3 h-3 text-vibe-accent" />}
                  </span>
                </td>
                <td className="py-1 pr-3 font-mono">{h.certName}</td>
                <td className={`py-1 pr-3 ${h.sanCovered === false ? 'text-vibe-error' : 'text-vibe-text-secondary'}`}>
                  {h.sanCovered === null ? '—' : h.sanCovered ? '✓' : '✗'}
                </td>
                <td className={`py-1 pr-3 ${h.sanCovered === false ? 'text-vibe-error' : 'text-vibe-text-secondary'}`}>
                  {h.certState}
                  {h.lastError ? ` — ${h.lastError}` : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/**
 * Gap A2a (2026-08-18) — the deploy row's OWN record (DB fields off the list
 * response, zero extra fetches). Live host/cert truth is the Coherence
 * panel's job; this answers "when did what happen to this deploy".
 */
function IdentityPanel({ deploy }: { deploy: DeployListItem }) {
  const at = (iso: string | null | undefined) =>
    iso ? new Date(iso).toLocaleString() : '—';
  const fields: Array<[string, string]> = [
    ['Project', deploy.projectId],
    ['First deployed', at(deploy.deployedAt)],
    ['Content last synced', at(deploy.distSyncedAt)],
    ['HTTP since', at(deploy.nginxHttpAt)],
    ['HTTPS since', at(deploy.nginxHttpsAt)],
  ];
  return (
    <div className="py-3 pl-1 space-y-1" data-testid={`identity-panel-${deploy.slug}`}>
      {fields.map(([label, value]) => (
        <div key={label} className="text-xs flex gap-2">
          <span className="text-vibe-text-secondary w-36 shrink-0">{label}</span>
          <span className="font-mono">{value}</span>
        </div>
      ))}
      {deploy.lastError ? (
        <div className="text-xs flex gap-2">
          <span className="text-vibe-text-secondary w-36 shrink-0">Last error</span>
          <span className="font-mono text-vibe-error">{deploy.lastError}</span>
        </div>
      ) : null}
    </div>
  );
}

export function DeploysShell() {
  const deploys = useDeployStore((s) => s.deploys);
  const fetchDeploys = useDeployStore((s) => s.fetchDeploys);
  const undeployProject = useDeployStore((s) => s.undeployProject);
  const fetchCoherence = useDeployStore((s) => s.fetchCoherence);
  const coherence = useDeployStore((s) => s.coherence);
  const rollbackProject = useDeployStore((s) => s.rollbackProject);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [openCoherenceId, setOpenCoherenceId] = useState<string | null>(null);
  // Gap A2a — identity is pure list data, so the toggle fetches nothing.
  const [openIdentityId, setOpenIdentityId] = useState<string | null>(null);
  // Gap A3 — rollback is live-site mutation, so it gets the same two-click
  // confirm the destructive Undeploy has. Errors (409 = no snapshot) render
  // inline per row instead of vanishing into the console.
  const [rollbackConfirmId, setRollbackConfirmId] = useState<string | null>(null);
  const [rollingBackId, setRollingBackId] = useState<string | null>(null);
  const [rollbackErrors, setRollbackErrors] = useState<Record<string, string>>({});

  const handleRollback = async (projectId: string) => {
    setRollbackConfirmId(null);
    setRollingBackId(projectId);
    setRollbackErrors((e) => ({ ...e, [projectId]: '' }));
    const res = await rollbackProject(projectId);
    if (!res.ok) {
      setRollbackErrors((e) => ({ ...e, [projectId]: res.error ?? 'rollback failed' }));
    }
    await fetchDeploys();
    setRollingBackId(null);
  };

  const toggleCoherence = (projectId: string) => {
    if (openCoherenceId === projectId) {
      setOpenCoherenceId(null);
      return;
    }
    setOpenCoherenceId(projectId);
    void fetchCoherence(projectId);
  };

  useEffect(() => {
    void fetchDeploys();
  }, [fetchDeploys]);

  // Publish an on-screen snapshot so the voice ("BRIDGE") and Pi agents can SEE
  // this tenant's deployments. GATED on the Deploys view being active — all
  // shells stay mounted under the display:none toggle, so only the visible one
  // owns the shared snapshot; `viewMode` in the deps re-publishes on switching
  // back here after a sibling view overwrote it. (See ghl/App.tsx.)
  const viewMode = useUIStore((s) => s.viewMode);
  const setSpaContext = useSpaContextStore((s) => s.setSpaContext);
  useEffect(() => {
    if (viewMode !== 'deploys') return;
    setSpaContext(buildDeploysSnapshot(deploys));
  }, [viewMode, deploys, setSpaContext]);

  // INTERACT — benign local UI only: let the agents refresh the list on command
  // (vibe:module-action). Undeploy is destructive → stays a two-click human
  // confirm in the row, never on the bus.
  useEffect(() => {
    const onModuleAction = (e: Event) => {
      const d = (e as CustomEvent).detail as { action?: string } | undefined;
      if (viewMode === 'deploys' && d?.action === 'refresh') void fetchDeploys();
    };
    window.addEventListener('vibe:module-action', onModuleAction);
    return () => window.removeEventListener('vibe:module-action', onModuleAction);
  }, [viewMode, fetchDeploys]);

  const handleUndeploy = async (projectId: string) => {
    setRemovingId(projectId);
    setConfirmId(null);
    await undeployProject(projectId);
    await fetchDeploys();
    setRemovingId(null);
  };

  return (
    <div
      data-testid="deploys-shell-root"
      className="flex flex-col h-full w-full overflow-hidden bg-vibe-bg text-vibe-text"
    >
      {/* TopBar hoisted to App level (2026-07-03) — voice strip stays here. */}
      <ResizableVoicePanel />
      <div className="flex-1 overflow-auto p-6">
        <div className="flex items-center gap-2 mb-1">
          <Globe className="w-5 h-5 text-vibe-accent" />
          <h2 className="text-lg font-semibold">Deployments</h2>
        </div>
        <p className="text-sm text-vibe-text-secondary mb-4">
          Public sites deployed for this tenant. Undeploy takes the URL down;
          the vhost and files are retired on the VPS (kept, not deleted).
        </p>
        {deploys.length === 0 ? (
          <div data-testid="deploys-empty" className="text-vibe-text-secondary text-sm">
            No active deployments. Use the <strong>Deploy</strong> button to
            publish a project.
          </div>
        ) : (
          <table
            className="w-full text-sm border-collapse"
            data-testid="deploys-table"
          >
            <thead>
              <tr className="text-left text-vibe-text-secondary border-b border-vibe-border">
                <th className="py-2 pr-4">Slug</th>
                <th className="py-2 pr-4">URL</th>
                <th className="py-2 pr-4">Status</th>
                <th className="py-2 pr-4">Action</th>
              </tr>
            </thead>
            <tbody>
              {deploys.map((d) => (
                <Fragment key={d.projectId}>
                <tr
                  data-testid={`deploy-row-${d.slug}`}
                  className="border-b border-vibe-border/50"
                >
                  <td className="py-2 pr-4 font-mono">{d.slug}</td>
                  <td className="py-2 pr-4">
                    <a
                      href={d.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-vibe-accent hover:underline inline-flex items-center gap-1"
                    >
                      {d.url}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </td>
                  <td className="py-2 pr-4">{d.status}</td>
                  <td className="py-2 pr-4">
                    {removingId === d.projectId ? (
                      <span className="text-vibe-text-secondary">Removing…</span>
                    ) : confirmId === d.projectId ? (
                      <span className="inline-flex items-center gap-2">
                        <button
                          data-testid={`undeploy-confirm-${d.slug}`}
                          onClick={() => void handleUndeploy(d.projectId)}
                          className="px-2 py-1 rounded bg-vibe-error/20 hover:bg-vibe-error/30 text-vibe-error"
                        >
                          Confirm take-down
                        </button>
                        <button
                          onClick={() => setConfirmId(null)}
                          className="px-2 py-1 rounded text-vibe-text-secondary hover:text-vibe-text"
                        >
                          Cancel
                        </button>
                      </span>
                    ) : rollingBackId === d.projectId ? (
                      <span className="text-vibe-text-secondary">Rolling back…</span>
                    ) : rollbackConfirmId === d.projectId ? (
                      <span className="inline-flex items-center gap-2">
                        <button
                          data-testid={`rollback-confirm-${d.slug}`}
                          onClick={() => void handleRollback(d.projectId)}
                          className="px-2 py-1 rounded bg-vibe-warning/20 hover:bg-vibe-warning/30 text-vibe-warning"
                        >
                          Confirm rollback (swaps live site to .prev)
                        </button>
                        <button
                          onClick={() => setRollbackConfirmId(null)}
                          className="px-2 py-1 rounded text-vibe-text-secondary hover:text-vibe-text"
                        >
                          Cancel
                        </button>
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-2">
                        <button
                          data-testid={`identity-toggle-${d.slug}`}
                          title="This deploy's own record: project binding + deploy/sync/HTTP/HTTPS timestamps."
                          onClick={() =>
                            setOpenIdentityId(openIdentityId === d.projectId ? null : d.projectId)
                          }
                          className="inline-flex items-center gap-1 px-2 py-1 rounded bg-vibe-bg-tertiary hover:bg-vibe-border text-vibe-text"
                        >
                          <Fingerprint className="w-3.5 h-3.5" />
                          Identity
                        </button>
                        <button
                          data-testid={`coherence-toggle-${d.slug}`}
                          onClick={() => toggleCoherence(d.projectId)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded bg-vibe-bg-tertiary hover:bg-vibe-border text-vibe-text"
                        >
                          <ShieldCheck className="w-3.5 h-3.5" />
                          Coherence
                        </button>
                        <button
                          data-testid={`rollback-${d.slug}`}
                          title="Swap the previous published docroot (.prev) back into place. Running it twice returns to where you started."
                          onClick={() => setRollbackConfirmId(d.projectId)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded bg-vibe-bg-tertiary hover:bg-vibe-border text-vibe-text"
                        >
                          <Undo2 className="w-3.5 h-3.5" />
                          Rollback
                        </button>
                        <button
                          data-testid={`undeploy-${d.slug}`}
                          onClick={() => setConfirmId(d.projectId)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded bg-vibe-bg-tertiary hover:bg-vibe-border text-vibe-text"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Undeploy
                        </button>
                      </span>
                    )}
                    {rollbackErrors[d.projectId] ? (
                      <div
                        data-testid={`rollback-error-${d.slug}`}
                        className="mt-1 text-xs text-vibe-error"
                      >
                        {rollbackErrors[d.projectId]}
                      </div>
                    ) : null}
                  </td>
                </tr>
                {openIdentityId === d.projectId && (
                  <tr className="border-b border-vibe-border/50">
                    <td colSpan={4} className="bg-vibe-bg-tertiary/30 px-2">
                      <IdentityPanel deploy={d} />
                    </td>
                  </tr>
                )}
                {openCoherenceId === d.projectId && (
                  <tr className="border-b border-vibe-border/50">
                    <td colSpan={4} className="bg-vibe-bg-tertiary/30 px-2">
                      <CoherencePanel slug={d.slug} entry={coherence[d.projectId]} />
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
